/**
 * Een tekst bekijken, in een venster: de tekst op elk niveau (3F is de
 * standaardtekst), en de toetsen die er al bij zijn.
 *
 * De tekst op elk niveau maakt en keurt de beheerder vooraf goed (stap 18,
 * tabblad Niveauteksten); de leerkracht ziet alleen teksten die daarmee
 * klaar zijn. Een toets ontstaat pas als de leerkracht een les met deze
 * tekst aanmaakt.
 *
 * Bij een verhaal in vijf deelteksten (historische fictie, of fictie van nu)
 * is er een derde tabblad, Verhaalopzet: de karakters, de actanten, het
 * vertelplan, de bouwstenen, de modelreconstructie, de causale eindvraag, de
 * verantwoording, en de andere versies van hetzelfde verhaal in een andere
 * tijd (components/Verhaalopzet.js).
 */
import React, { useEffect, useState } from 'react';
import { BookOpen, ClipboardList, FileText, X } from 'lucide-react';
import { leerkrachtApi as api } from '../../../api/leerkracht';
import { Venster } from '../../../components/Venster';
import { Laden } from '../../../components/Laden';
import { zonderKopjes } from '../../../components/OpgemaakteTekst';
import { GemarkeerdeTekst, Verhaalopzet, heeftMarkeringen } from '../../../components/Verhaalopzet';
import { TekstMaten } from '../../../components/TekstMaten';
import { genreVan, isDakpan, isVerhaal } from '../../../keuzelijsten';
import { DakpanOpzet } from '../../../components/DakpanOpzet';

export function TekstVoorbeeld({ tekstId, onSluit, meldFout }) {
  const [detail, setDetail] = useState(null);
  const [inhoud, setInhoud] = useState(null);
  const [niveaus, setNiveaus] = useState([]);
  const [niveau, setNiveau] = useState(null);
  const [laden, setLaden] = useState(false);
  const [tab, setTab] = useState('tekst');           // tekst of toets
  const [toetsen, setToetsen] = useState([]);
  const [toetsNiveau, setToetsNiveau] = useState(null);
  const [bouwstenen, setBouwstenen] = useState(false);   // historische fictie: de markeringen tonen

  useEffect(() => {
    const open = async () => {
      setLaden(true);
      try {
        const [gegevens, bijTekst] = await Promise.all([api.haalTekst(tekstId), api.haalToetsenBijTekst(tekstId)]);
        setDetail(gegevens);
        setToetsen(bijTekst);
        // De standaardtekst (3F) eerst, dan wat er gemaakt is.
        setNiveaus(['3F', ...gegevens.gegenereerde_niveaus.filter(n => n !== '3F')]);
        setNiveau('3F');
        if (bijTekst.length > 0) setToetsNiveau(bijTekst[0].niveau);
        setInhoud(await api.haalTekstOpNiveau(tekstId, '3F'));
      } catch (err) {
        meldFout(err.message);
      } finally {
        setLaden(false);
      }
    };
    open();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen
  }, [tekstId]);

  const kiesNiveau = async (nieuw) => {
    setNiveau(nieuw);
    setLaden(true);
    try {
      setInhoud(await api.haalTekstOpNiveau(tekstId, nieuw));
    } catch (err) {
      meldFout(err.message);
    } finally {
      setLaden(false);
    }
  };

  // Tot de gegevens er zijn, is er (zoals voorheen) nog geen venster.
  if (!detail) return null;

  const toets = toetsen.find(q => q.niveau === toetsNiveau);

  return (
    <Venster breed={1000}>
      <div className="flex-between mb-16">
        <h2>{detail.titel}</h2>
        <button className="btn btn-secondary" onClick={onSluit}><X size={16} /> Sluiten</button>
      </div>

      <div className="card mb-16" style={{ background: 'var(--vlak)', padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '14px' }}>
          {detail.beschrijving && <span><strong>Beschrijving:</strong> {detail.beschrijving}</span>}
          {detail.text_type && <span><strong>Type:</strong> {genreVan(detail) ? genreVan(detail).label.toLowerCase() : detail.text_type}</span>}
          {detail.auteur && <span><strong>Auteur:</strong> {detail.auteur}</span>}
          <span><strong>Paragrafen:</strong> {detail.paragraph_count}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid var(--lijn)', paddingBottom: 12 }}>
        <button className={`btn ${tab === 'tekst' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('tekst')}>
          <FileText size={16} /> Tekst
        </button>
        <button className={`btn ${tab === 'toets' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('toets')}>
          <ClipboardList size={16} /> Toetsen ({toetsen.length})
        </button>
        {(isVerhaal(detail) || isDakpan(detail)) && detail.opzet && (
          <button className={`btn ${tab === 'opzet' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('opzet')}>
            <BookOpen size={16} /> {isDakpan(detail) ? 'Opzet van de set' : 'Verhaalopzet'}
          </button>
        )}
      </div>

      {tab === 'opzet' && (
        <div style={{ maxHeight: '500px', overflow: 'auto' }}>
          {isDakpan(detail) ? <DakpanOpzet opzet={detail.opzet} />
            : <Verhaalopzet opzet={detail.opzet} genre={detail.genre} verwant={detail.verwant} />}
        </div>
      )}

      {tab === 'tekst' && (
        <>
          <div className="card mb-16" style={{ background: 'var(--blauw-vlak)', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <strong>Bekijk niveau:</strong>
                <select className="input" style={{ padding: '4px 8px', fontSize: '13px', minWidth: '180px' }}
                  value={niveau || ''} onChange={(e) => kiesNiveau(e.target.value)} disabled={laden}>
                  {niveaus.map(n => <option key={n} value={n}>{n === '3F' ? '3F (origineel)' : n}</option>)}
                </select>
              </div>
              <span className="text-muted">|</span>
              <span>
                <strong>Goedgekeurde niveaus:</strong>{' '}
                {detail.gegenereerde_niveaus.length > 0 ? detail.gegenereerde_niveaus.join(', ') : 'Geen (alleen origineel beschikbaar)'}
              </span>
            </div>
          </div>

          {laden ? (
            <Laden tekst="Tekst laden..." />
          ) : inhoud && (
            <>
              <div className="card mb-8" style={{ background: inhoud.is_generated ? 'var(--goed-vlak)' : 'var(--waarschuw-vlak)', padding: '8px 12px' }}>
                <small>
                  {inhoud.is_generated ? '✓ Goedgekeurd' : '📄 Origineel'} |{' '}
                  Niveau: {inhoud.niveau} |{' '}
                  Woorden: <strong>{inhoud.tekstlengte_werkelijk}</strong>
                </small>
                <TekstMaten maten={inhoud.maten} niveau={inhoud.is_generated ? inhoud.niveau : '3F'} />
                {heeftMarkeringen(inhoud.paragraphs) && (
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, fontSize: 13 }}>
                    <input type="checkbox" checked={bouwstenen} onChange={e => setBouwstenen(e.target.checked)} />
                    Bouwstenen tonen
                  </label>
                )}
              </div>
              <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid var(--lijn-2)', padding: '16px', borderRadius: '4px' }}>
                {inhoud.paragraphs.map(p => (
                  <p key={p.id} style={{ marginBottom: '16px', lineHeight: 1.7 }}>
                    {bouwstenen && p.gemarkeerd ? <GemarkeerdeTekst tekst={zonderKopjes(p.gemarkeerd)} /> : zonderKopjes(p.inhoud)}
                  </p>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'toets' && (
        toetsen.length === 0 ? (
          <div className="text-center" style={{ padding: '40px' }}>
            <p className="text-muted">Geen toetsen gegenereerd voor deze tekst.</p>
            <p className="text-muted" style={{ fontSize: '13px' }}>Toetsen worden aangemaakt wanneer je een les maakt met deze tekst.</p>
          </div>
        ) : (
          <>
            <div className="card mb-16" style={{ background: 'var(--blauw-vlak)', padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <strong>Bekijk toets voor niveau:</strong>
                <select className="input" style={{ padding: '4px 8px', fontSize: '13px', minWidth: '120px' }}
                  value={toetsNiveau || ''} onChange={(e) => setToetsNiveau(e.target.value)}>
                  {toetsen.map(q => <option key={q.niveau} value={q.niveau}>{q.niveau}</option>)}
                </select>
              </div>
            </div>
            {toets && <Toets toets={toets} />}
          </>
        )
      )}
    </Venster>
  );
}

function Toets({ toets }) {
  return (
    <div style={{ maxHeight: '450px', overflow: 'auto' }}>
      {toets.mc_vragen.length > 0 && (
        <>
          <h4 style={{ marginBottom: 12 }}>Multiple choice vragen ({toets.mc_vragen.length})</h4>
          {toets.mc_vragen.map((vraag, i) => (
            <div key={i} style={{ marginBottom: 16, padding: 16, background: 'var(--vlak)', borderRadius: 8, borderLeft: '4px solid var(--accent)' }}>
              <p style={{ fontWeight: 500, marginBottom: 8 }}>Vraag {i + 1}: {vraag.vraag}</p>
              <div style={{ marginLeft: 16, marginBottom: 8 }}>
                {Object.entries(vraag.opties).map(([letter, tekst]) => {
                  const goed = letter === vraag.correct_antwoord;
                  return (
                    <div key={letter} style={{ padding: '4px 0', color: goed ? 'var(--goed)' : 'var(--tekst)', fontWeight: goed ? 'bold' : 'normal' }}>
                      {letter}. {tekst} {goed && '✓'}
                    </div>
                  );
                })}
              </div>
              {vraag.uitleg && <p style={{ fontSize: 13, color: 'var(--tekst-3)', fontStyle: 'italic' }}>Uitleg: {vraag.uitleg}</p>}
            </div>
          ))}
        </>
      )}

      {toets.open_vragen.length > 0 && (
        <>
          <h4 style={{ marginTop: 24, marginBottom: 12 }}>Open vragen ({toets.open_vragen.length})</h4>
          {toets.open_vragen.map((vraag, i) => (
            <div key={i} style={{ marginBottom: 12, padding: 16, background: 'var(--vlak)', borderRadius: 8, borderLeft: '4px solid var(--waarschuw)' }}>
              <p style={{ fontWeight: 500 }}>Vraag {i + 1}: {vraag.vraag}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
